import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, RouterStateSnapshot } from '@angular/router';
import { Observable, of as observableOf } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { AttemptedPathService } from '@app/core/attempted-path.service';
import { REDEEM_MEMBERSHIP_FROM_EMAIL } from '@app/core/forgot-password/forgot-password.component';
import { AUTH_GUARD_TYPE_NAME, HIDE_LWA_PARAM_VALUE } from '@app/core/global-constants';
import { CLAIM_CODE_PARAM_KEY } from '@app/shared/hornbill-params';

import { AnalyticsService } from './analytics.service';
import { AuthService, AuthServiceLoginArgs } from './auth.service';
import { FeatureFlags } from './feature-flags/feature-flags';
import { LaunchDarklyService } from './feature-flags/launchdarkly.service';

@Injectable({
  providedIn: 'root',
})
export class AuthGuardService implements CanActivate {
  constructor(
    private attemptedPathService: AttemptedPathService,
    private authService: AuthService,
    private analyticsService: AnalyticsService,
    private launchDarklyService: LaunchDarklyService,
  ) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    return this.authService.isLoggedIn$().pipe(
      map(isLoggedIn => isLoggedIn || this.handleUnauthenticated(state.url, route)),
      catchError(() => observableOf(this.handleUnauthenticated(state.url, route))),
    );
  }

  private handleUnauthenticated(url: string, route: ActivatedRouteSnapshot): boolean {
    url = url.replace('login_with_amazon=false', '');
    if (url.includes('/account/security?dismiss=true')) {
      url = '/';
    }
    this.analyticsService.trackDirectLinksToLoggedInResources(url);
    this.attemptedPathService.setAttemptedPath(url);

    const loginWithAmazonParam = route.queryParams['login_with_amazon'];
    const hideLWAParam = route.queryParams['hideLWA'];

    let loginArgs: AuthServiceLoginArgs = {
      path: url,
      trackingSource: AUTH_GUARD_TYPE_NAME,
    };

    const customAuthorizationParams: any = {};

    if (loginWithAmazonParam) {
      customAuthorizationParams.login_with_amazon = loginWithAmazonParam;
    }

    this.launchDarklyService
      .featureFlag$(FeatureFlags.HIDE_LWA, false)
      .pipe(catchError(() => observableOf(false)))
      .subscribe(hideLWAFeatureEnabled => {
        if (hideLWAFeatureEnabled && hideLWAParam === HIDE_LWA_PARAM_VALUE) {
          customAuthorizationParams.hideLWA = HIDE_LWA_PARAM_VALUE;
        }

        if (Object.keys(customAuthorizationParams).length > 0) {
          loginArgs = { ...loginArgs, customAuthorizationParams };
        }

        if (!!route.queryParams[CLAIM_CODE_PARAM_KEY]) {
          loginArgs = { ...loginArgs, fragment: REDEEM_MEMBERSHIP_FROM_EMAIL };
        }

        this.authService.goLogin(loginArgs);
      });

    return false;
  }
}
